import { Grid, Stack } from "@mui/material";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect } from "react";
import { Outlet } from "react-router";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import biApi from "../../../../api/biApi";
import useClientContext from "../../../../context/useClientContext";
import { BiApiClientProvider } from "../sv/contexts/BiApiClientProvider.tsx";
import type { BiApiClientBase } from "../sv/contexts/BiApiClientProviderContext.ts";
import ReportsManager from "./contexts/reports-manager/ReportsManager";
import { ReportsContextProvider } from "./contexts/reports/ReportsContextProvider";
import { ReportTemplatesContextProvider } from "./contexts/ReportTemplatesContextProvider.tsx";
import { GroupsContextProvider } from "./groups/contexts/GroupsContextProvider.tsx";
import useGroups from "./groups/hooks/useGroups.ts";
import useReports from "./hooks/useReports";
import { getEditUiReportUrl, getViewUiReportUrl } from "./reports/utilities/editReportUrl";
import useReportTemplates from "./templates/hooks/useReportTemplates";

export default function CompanyUiReportsPage() {
  const { clientCode } = useClientContext();
  const loadReports = useCallback((signal?: AbortSignal) => biApi.uiReports.getReports({}, signal), []);
  const { reports, ui: reportsUi, actions: reportsActions } = useReports(loadReports, "Ui");
  const {
    groups,
    actions: groupActions,
    ui: groupsUi,
  } = useGroups({
    ...biApi.uiReportGroup,
    applyGroupSharing: reportsActions.applyGroupSharing,
  });

  const {
    templates,
    actions: reportTemplateActions,
    ui: templatesUi,
  } = useReportTemplates({
    getTemplates: biApi.getCompanyReportTemplates,
  });

  useEffect(() => {
    groupActions.load(clientCode);
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [clientCode, groupActions.load]);

  if (!reportsUi.loaded || !groupsUi.loaded) {
    return (
      <FullSize>
        <InlineLoader text="Loading reports..." />
      </FullSize>
    );
  }

  if (reportsUi.error !== undefined || groupsUi.error !== undefined) {
    return (
      <FullSize>
        <DataLoadingFailed />
      </FullSize>
    );
  }

  return (
    <Grid container sx={{ flex: 1, height: "100%", width: "100%" }}>
      <BiApiClientProvider<BiApiClientBase>
        api={{
          validateName: biApi.uiReports.validateReportName,
          getReportSharing: biApi.uiReports.getReportSharing,
          updateReportSharing: biApi.uiReports.updateReportSharing,
          getEditReportUrl: getEditUiReportUrl,
          getViewReportUrl: getViewUiReportUrl,
        }}
      >
        <ReportTemplatesContextProvider
          groupsUi={{ loaded: true }}
          actions={reportTemplateActions}
          reportTemplates={templates}
          ui={templatesUi}
        >
          <ReportsContextProvider reports={reports} ui={reportsUi} actions={reportsActions}>
            <GroupsContextProvider
              groups={groups}
              ui={groupsUi}
              actions={{ ...groupActions, reloadReports: reportsActions.reloadReports }}
            >
              <ReportsManager reports={reports} reportUsage={"Ui"}>
                <Outlet />
              </ReportsManager>
            </GroupsContextProvider>
          </ReportsContextProvider>
        </ReportTemplatesContextProvider>
      </BiApiClientProvider>
    </Grid>
  );
}
function FullSize({ children }: PropsWithChildren) {
  return <Stack sx={{ height: "100%" }}>{children}</Stack>;
}
